import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { receiptViewActions } from '../store/receiptViewSlice';
import { useGetPendingReceiptByIdQuery } from '../store/services/receiptsServices';
import { CircularProgress } from '@mui/material';
import { LuArrowLeft, LuArrowRight, LuX, LuZoomIn, LuZoomOut, LuRefreshCw, LuImageOff } from "react-icons/lu";
import { useEffect, useState } from 'react';
import { userDetailsActions } from '../store/userDetailsSlice';
import UserDetails from './users/UserDetails';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { RECEIPT_TYPES, isSefReceipt } from '../utils/receiptFields';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '0px',
        padding: '32px 24px',
        boxShadow: '2px 5px 10px #444',
    },
    overlay: {
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: "blur(2px)"
    }
};

const imageZoomModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '8px',
        padding: '0',
        boxShadow: '2px 5px 20px #000',
        width: '90vw',
        height: '90vh',
        maxWidth: '1200px',
        maxHeight: '800px',
    },
    overlay: {
        background: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: "blur(4px)",
        zIndex: 9999
    }
};

Modal.setAppElement(document.getElementById('root'))

export default function ReceiptView() {

    const dispatch = useDispatch()
    const receipt = useSelector(state => state.receipt_view)
    const isOpen = receipt.isOpen
    const isUserDetailsOpen = useSelector(state => state.user_details.isOpen)

    const { data, isLoading } = useGetPendingReceiptByIdQuery({ id: receipt.receipt.receipt_id })

    /* A new image gets a fresh chance to load */
    useEffect(() => {
        setImageError(false)
    }, [data?.data?.image_path])

    const [isOriginalCouponShown, setIsOriginalCouponShown] = useState(false)
    const [imageLoading, setImageLoading] = useState(false)
    const [imageError, setImageError] = useState(false)
    const [isImageZoomOpen, setIsImageZoomOpen] = useState(false)

    // useEffect(() => {
    //     if (data) {
    //         if (data.image_path) {
    //             if (Array.isArray(data.image_path)) {
    //                 setImages(data.image_path)
    //             }
    //             else {
    //                 setImages(JSON.parse(data.image_path))
    //             }
    //         }
    //     }
    // }, [data])

    function closeModal() {
        dispatch(receiptViewActions.closeModal())
    }

    const r = data?.data
    const isSef = isSefReceipt(r)
    /* PEF / not-physical receipts are a photo: it's the receipt itself, so it's always shown */
    const showImage = !isSef || isOriginalCouponShown
    const noValue = (text) => <span className='font-light italic text-gray-400'>{text}</span>

    const Row = ({ label, children }) => (
        <div className='flex items-center justify-between gap-4'>
            <div className='font-light'>{label}</div>
            <div className='font-light text-right break-all'>{children}</div>
        </div>
    )

    return (
        <div>
            {isUserDetailsOpen && <UserDetails opacity={0} />}
            <Modal
                isOpen={isOpen}
                onRequestClose={closeModal}
                style={customStyles}
                contentLabel="Detajet e kuponit"
            >
                <div className={`relative flex items-center gap-8 bg-white ${showImage ? 'w-[560px]' : 'w-[280px]'} scrollbar-thin scrollbar-track scrollbar-thumb`}>
                    {isLoading && (
                        <div className='absolute flex items-center justify-center top-0 left-0 w-full h-full bg-white z-20 text-secondary'>
                            <CircularProgress color='var(--secondary-color)' size={40} />
                        </div>
                    )}
                    <div className='flex gap-8 h-full w-full'>
                        <div className='h-full flex flex-col gap-2 justify-between w-full text-sm'>
                            <div className='text-xl text-center uppercase font-bold'>
                                {r?.business_name || (!isSef && noValue('Biznesi caktohet pas aprovimit'))}
                            </div>
                            <hr className='border-dashed border-gray-400' />
                            <div className='text-lg text-center uppercase font-light'>
                                {r?.type === RECEIPT_TYPES.NON_FISCAL ? 'Kupon jo fiskal' : 'Kupon fiskal'}
                                {r?.type && <span className='text-xs text-gray-400'> · {r.type}</span>}
                            </div>
                            <hr className='border-dashed border-gray-400' />
                            <Row label='ID'>{r?.receipt_id}</Row>
                            <Row label='NUI'>{r?.NUI || noValue(isSef ? 'Pa NUI' : 'Caktohet pas aprovimit')}</Row>
                            {isSef ? (
                                <>
                                    <Row label='Numri i kuponit'>{r?.coupon_id || noValue('Pa numër kuponi')}</Row>
                                    <Row label='Nr. i pajisjes SEF'>{r?.sef_device_number || noValue('Pa numër pajisjeje')}</Row>
                                    <Row label='Data e lëshimit'>{r?.release_date || noValue('—')}</Row>
                                    <hr className='border-dashed border-gray-400' />
                                    <div className='max-h-[200px] overflow-y-scroll tiny-scrollbar pr-2 font-light'>
                                        {r?.items?.length > 0 ? (
                                            r.items.map((product, i) => (
                                                <div key={i} className='flex justify-between items-center gap-3' title={`${product.Quantity}x ${product.Name}`}>
                                                    <div className='flex min-w-0'>
                                                        <span className='shrink-0'>{product.Quantity}x&nbsp;</span>
                                                        <span className='truncate'>{product.Name}</span>
                                                    </div>
                                                    <div className='shrink-0 tabular-nums'>{(product.Total / 100).toFixed(2)}€</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className='italic text-gray-400'>Produktet nuk u gjetën</div>
                                        )}
                                    </div>
                                    <hr className='border-dashed border-gray-400' />
                                    <Row label='Totali pa TVSH'>{r?.total_without_vat}€</Row>
                                    <Row label='TVSH'>{r?.vat_amount}€</Row>
                                </>
                            ) : (
                                <>
                                    <Row label='Nr. serik i PEF'>{r?.fiscal_device_number || noValue('Pa numër serik')}</Row>
                                    <hr className='border-dashed border-gray-400' />
                                </>
                            )}
                            <div className='flex items-center justify-between'>
                                <div className='text-xl font-bold'>Total</div>
                                <div className='text-xl font-bold'>{r?.total_with_vat}€</div>
                            </div>
                            <hr className='border-dashed border-gray-400' />
                            <div className='flex items-center justify-between'>
                                <div className='font-light'>Përdoruesi</div>
                                <div className='font-light hover:underline cursor-pointer' onClick={() => dispatch(userDetailsActions.openModal({ id: r?.user_id }))}>{r?.first_name} {r?.last_name}</div>
                            </div>
                            <Row label='Data e skenimit'>{r?.created_at && new Date(r.created_at).toLocaleString('en-GB', { timeZone: "Europe/Amsterdam" })}</Row>
                            {/* SEF receipts are read from the QR code; they only have a photo if one was attached */}
                            {isSef && r?.image_path && (
                                <div className='flex items-center justify-center gap-2 mt-4 hover:underline cursor-pointer' onClick={() => setIsOriginalCouponShown(!isOriginalCouponShown)}>
                                    {isOriginalCouponShown ? (
                                        <>
                                            <span>Fshih origjinalin</span>
                                            <span><LuArrowLeft size={10} /></span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Shiko origjinalin</span>
                                            <span><LuArrowRight size={10} /></span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {showImage && (
                        <div className='w-full h-full flex items-center justify-center relative'>
                            {imageLoading && (
                                <div className='absolute flex items-center justify-center w-full h-full bg-white z-10'>
                                    <CircularProgress size={40} />
                                </div>
                            )}
                            {r?.image_path && imageError ? (
                                <div className='flex flex-col items-center gap-2 text-center text-gray-400 px-6'>
                                    <LuImageOff size={40} />
                                    <div className='text-sm font-medium text-gray-500'>Fotoja e origjinalit nuk mund të ngarkohet</div>
                                    <button
                                        type='button'
                                        onClick={() => setImageError(false)}
                                        className='text-xs text-blue-600 hover:text-blue-800 hover:underline'
                                    >
                                        Provo përsëri
                                    </button>
                                </div>
                            ) : r?.image_path ? (
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_HOST}/${r.image_path}`}
                                    alt="Fotoja e origjinalit"
                                    className='object-cover w-full h-full cursor-pointer hover:opacity-90 transition-opacity'
                                    onLoadStart={() => setImageLoading(true)}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => {
                                        setImageLoading(false)
                                        setImageError(true)
                                    }}
                                    onClick={() => setIsImageZoomOpen(true)}
                                    title="Kliko për zmadhim"
                                />
                            ) : (
                                <div className='italic text-gray-300 text-sm'>Nuk ka foto të origjinalit</div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Image Zoom Modal */}
            <Modal
                isOpen={isImageZoomOpen}
                onRequestClose={() => setIsImageZoomOpen(false)}
                style={imageZoomModalStyles}
                contentLabel="Image Zoom View"
            >
                <div className='relative w-full h-full bg-black'>
                    <TransformWrapper
                        initialScale={1}
                        minScale={0.5}
                        maxScale={5}
                        centerOnInit={true}
                    >
                        {({ zoomIn, zoomOut, resetTransform }) => (
                            <>
                                {/* Control buttons */}
                                <div className='absolute top-4 right-4 flex gap-2 z-10'>
                                    <button
                                        onClick={() => zoomIn()}
                                        className='bg-white hover:bg-gray-200 text-gray-800 p-2 rounded-full shadow-lg transition-colors'
                                        title="Zoom In"
                                    >
                                        <LuZoomIn size={24} />
                                    </button>
                                    <button
                                        onClick={() => zoomOut()}
                                        className='bg-white hover:bg-gray-200 text-gray-800 p-2 rounded-full shadow-lg transition-colors'
                                        title="Zoom Out"
                                    >
                                        <LuZoomOut size={24} />
                                    </button>
                                    <button
                                        onClick={() => resetTransform()}
                                        className='bg-white hover:bg-gray-200 text-gray-800 p-2 rounded-full shadow-lg transition-colors'
                                        title="Reset"
                                    >
                                        <LuRefreshCw size={24} />
                                    </button>
                                    <button
                                        onClick={() => setIsImageZoomOpen(false)}
                                        className='bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors'
                                        title="Close"
                                    >
                                        <LuX size={24} />
                                    </button>
                                </div>

                                {/* Image container */}
                                <TransformComponent
                                    wrapperClass='!w-full !h-full'
                                    contentClass='!w-full !h-full flex items-center justify-center'
                                >
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_HOST}/${data?.data.image_path}`}
                                        alt="Receipt Original"
                                        className='max-w-full max-h-full object-contain'
                                    />
                                </TransformComponent>
                            </>
                        )}
                    </TransformWrapper>
                </div>
            </Modal>
        </div>
    )
}
